import { RouteObject } from 'react-router';
import { Route } from '@/core/types';
import { AccessGranted } from '@/features/auth/hooks/useAuthorization';
import ComposeScreen from '@/core/components/ComposeScreen';
import ErrorPage from '@/core/components/ErrorHandlers/ErrorPage';
import { findFirstAvailableRoute } from '@/core/pages/utils/findFirstAvailableRoute';

export const getChildRoutes = (
  routeArray: Route[],
  isAccessGranted: AccessGranted,
): RouteObject[] =>
  routeArray.reduce<RouteObject[]>((allRoutes, routes) => {
    // As these two functions are recursive, this rule has to be ignored
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const routesArr = getRoutesForRouter(routes, isAccessGranted);
    return [...allRoutes, ...routesArr];
  }, []);

const routeFactory = (path: Route['path'], route: Route): RouteObject => ({
  path,
  element: <ComposeScreen route={route} />,
  errorElement: <ErrorPage />,
});

const getRoutesForRouter = (route: Route, isAccessGranted: AccessGranted): RouteObject[] => {
  const { path, additionalPaths } = route;
  const mainRoute = routeFactory(path, route);
  if (route.routes && route.routes.length > 0) {
    mainRoute.children = getChildRoutes(route.routes, isAccessGranted);

    // Render the first-available child's element directly at the parent path,
    // so visiting `/business-account` (or `/business-account/`) lands on the
    // right page without a redirect round-trip.
    const firstAvailable = findFirstAvailableRoute(route.routes, isAccessGranted);
    if (firstAvailable) {
      mainRoute.children.push({
        path,
        element: firstAvailable.element,
        errorElement: <ErrorPage />,
      });
    }
  }

  const otherRoutes = (additionalPaths || []).map((additionalPath) =>
    routeFactory(additionalPath, route),
  );

  return [mainRoute, ...otherRoutes];
};
